import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Calculator, ArrowRight, Phone, CheckCircle, Globe, 
  TrendingUp, Users, FileText, Shield
} from 'lucide-react';

// Calculator configurations with URLs
const CALCULATORS = [
  { 
    id: 'canada', 
    name: 'Canada Express Entry (CRS)', 
    shortName: 'Canada CRS',
    flag: '🇨🇦', 
    url: '/canada-crs-calculator',
    description: 'Calculate your Comprehensive Ranking System score for Express Entry immigration to Canada.',
    passingScore: '470+',
    maxScore: '1200',
    color: 'from-red-500/20 to-red-900/20',
    borderColor: 'border-red-500/30',
    hoverBorder: 'hover:border-red-500/50'
  },
  { 
    id: 'quebec', 
    name: 'Quebec Skilled Worker (QSWP)', 
    shortName: 'Quebec',
    flag: '🇨🇦', 
    url: '/quebec-points-calculator',
    description: 'Assess your eligibility for Quebec Skilled Worker Program through Arrima.',
    passingScore: '50+',
    maxScore: '123',
    color: 'from-blue-500/20 to-blue-900/20',
    borderColor: 'border-blue-500/30',
    hoverBorder: 'hover:border-blue-500/50'
  },
  { 
    id: 'australia', 
    name: 'Australia Skilled Migration (GSM)', 
    shortName: 'Australia',
    flag: '🇦🇺', 
    url: '/australia-points-calculator',
    description: 'Check your points for Subclass 189, 190, and 491 skilled migration visas.',
    passingScore: '65+',
    maxScore: '130',
    color: 'from-blue-500/20 to-yellow-900/20',
    borderColor: 'border-blue-500/30',
    hoverBorder: 'hover:border-yellow-500/50'
  },
  { 
    id: 'uk', 
    name: 'UK Skilled Worker Visa', 
    shortName: 'UK',
    flag: '🇬🇧', 
    url: '/uk-points-calculator',
    description: 'Calculate your points for the UK points-based Skilled Worker visa system.',
    passingScore: '70',
    maxScore: '70',
    color: 'from-indigo-500/20 to-indigo-900/20',
    borderColor: 'border-indigo-500/30',
    hoverBorder: 'hover:border-indigo-500/50'
  },
  { 
    id: 'sinp', 
    name: 'Saskatchewan SINP', 
    shortName: 'Saskatchewan',
    flag: '🇨🇦', 
    url: '/sinp-points-calculator',
    description: 'Assess your eligibility for Saskatchewan Immigrant Nominee Program.',
    passingScore: '60+',
    maxScore: '100',
    color: 'from-green-500/20 to-green-900/20',
    borderColor: 'border-green-500/30',
    hoverBorder: 'hover:border-green-500/50'
  },
  { 
    id: 'germany', 
    name: 'Germany Chancenkarte', 
    shortName: 'Germany',
    flag: '🇩🇪', 
    url: '/germany-chancenkarte-calculator',
    description: 'Check your eligibility for Germany\'s new Opportunity Card (Chancenkarte) visa.',
    passingScore: '6+',
    maxScore: '14',
    color: 'from-yellow-500/20 to-yellow-900/20',
    borderColor: 'border-yellow-500/30',
    hoverBorder: 'hover:border-yellow-500/50'
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const CalculatorsPage = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet defer={false}>
        <title>Immigration Points Calculators 2026 | Canada, Australia, UK, Germany | Zafco HR</title>
        <meta name="description" content="Free immigration points calculators for Canada CRS, Quebec Skilled Worker, Australia GSM, UK Skilled Worker, Saskatchewan SINP, and Germany Chancenkarte. Calculate your eligibility instantly with official government criteria." />
        <meta name="keywords" content="immigration calculator, CRS calculator, points calculator, Canada PR, Australia PR, UK visa, Germany Chancenkarte, Quebec immigration, Saskatchewan SINP" />
        <link rel="canonical" href="https://zafco.in/calculators" />
        
        <meta property="og:title" content="Immigration Points Calculators 2026 | Zafco HR" />
        <meta property="og:description" content="Free immigration points calculators for Canada, Australia, UK, Germany, Quebec, and Saskatchewan. Official government criteria." />
        <meta property="og:url" content="https://zafco.in/calculators" />
        <meta property="og:type" content="website" />
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://zafco.in" },
              { "@type": "ListItem", "position": 2, "name": "Points Calculators", "item": "https://zafco.in/calculators" }
            ]
          })}
        </script>
        
        {/* ItemList Schema for calculators */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Immigration Points Calculators",
            "description": "Collection of free immigration points calculators for skilled worker programs worldwide",
            "numberOfItems": CALCULATORS.length,
            "itemListElement": CALCULATORS.map((calc, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "name": calc.name,
              "url": `https://zafco.in${calc.url}`
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-950" data-testid="calculators-hub-page">
        {/* Hero Section */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#C5A028]/10 to-transparent" />
          <div className="container max-w-7xl mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A028]/10 border border-[#C5A028]/30 rounded-full text-[#C5A028] text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Official Government Criteria
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-syne mb-6">
                Immigration Points Calculators
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Calculate your eligibility for skilled worker immigration programs in Canada, Australia, UK, and Germany. 
                Free, instant assessments based on official government criteria.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>6 Countries</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>100% Free</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Instant Results</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Updated 2026</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calculator Cards Grid */}
        <section className="py-12 md:py-16">
          <div className="container max-w-7xl mx-auto px-4">
            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {CALCULATORS.map((calc) => (
                <motion.div key={calc.id} variants={cardVariants}>
                  <Link 
                    to={calc.url}
                    className={`block h-full bg-gradient-to-br ${calc.color} border ${calc.borderColor} ${calc.hoverBorder} rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#C5A028]/5 group`}
                    data-testid={`calculator-link-${calc.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-5xl">{calc.flag}</span>
                      <ArrowRight className="w-6 h-6 text-slate-500 group-hover:text-[#C5A028] group-hover:translate-x-1 transition-all" />
                    </div>
                    
                    <h2 className="text-xl font-bold text-white mb-2 group-hover:text-[#C5A028] transition-colors">
                      {calc.name}
                    </h2>
                    
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                      {calc.description}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-700/50">
                      <div className="text-sm">
                        <span className="text-slate-500">Passing:</span>
                        <span className="text-white font-semibold ml-1">{calc.passingScore}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">Max:</span>
                        <span className="text-white font-semibold ml-1">{calc.maxScore}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center gap-2 text-[#C5A028] font-medium text-sm group-hover:gap-3 transition-all">
                      <Calculator className="w-4 h-4" />
                      Calculate Now
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Why Use Our Calculators */}
        <section className="py-16 bg-slate-900/50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white font-syne mb-4">
                Why Use Our Calculators?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Our calculators are designed by immigration experts and updated regularly to reflect the latest government criteria.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#C5A028]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-[#C5A028]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Official Criteria</h3>
                <p className="text-slate-400 text-sm">Based on official government immigration guidelines and scoring systems.</p>
              </div>
              
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#C5A028]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-[#C5A028]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Score Improvement Tips</h3>
                <p className="text-slate-400 text-sm">Get personalized suggestions on how to improve your immigration score.</p>
              </div>
              
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#C5A028]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-[#C5A028]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Multiple Countries</h3>
                <p className="text-slate-400 text-sm">Compare your eligibility across 6 popular immigration destinations.</p>
              </div>
              
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-[#C5A028]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-[#C5A028]" />
                </div>
                <h3 className="text-white font-semibold mb-2">Expert Support</h3>
                <p className="text-slate-400 text-sm">Connect with our immigration consultants for personalized guidance.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links Section - Additional crawlable links */}
        <section className="py-16">
          <div className="container max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-white font-syne mb-8 text-center">
              Quick Access to All Calculators
            </h2>
            
            {/* Text links for SEO crawlability */}
            <nav className="flex flex-wrap justify-center gap-4 mb-12" aria-label="Calculator navigation">
              {CALCULATORS.map((calc) => (
                <Link 
                  key={calc.id}
                  to={calc.url}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-[#C5A028]/50 rounded-lg text-slate-300 hover:text-white transition-colors text-sm"
                >
                  {calc.flag} {calc.shortName} Calculator
                </Link>
              ))}
            </nav>
            
            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#C5A028]/20 to-[#C5A028]/5 border border-[#C5A028]/30 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                Need Help With Your Immigration Journey?
              </h3>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Our experienced immigration consultants can help you maximize your points and guide you through the entire application process.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/visa-pre-assesment"
                  className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors font-medium"
                >
                  <FileText className="w-5 h-5" />
                  Free Assessment Form
                </Link>
                <a 
                  href="https://wa.me/919958848998"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-[#C5A028] hover:bg-[#d4af37] text-slate-900 rounded-lg transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  Talk to Expert
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Links for Additional SEO */}
        <footer className="bg-slate-900/50 border-t border-slate-800 py-8">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div>
                <h4 className="text-white font-semibold mb-4">Canada Immigration</h4>
                <ul className="space-y-2">
                  <li><Link to="/canada-crs-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">Canada CRS Calculator</Link></li>
                  <li><Link to="/quebec-points-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">Quebec Points Calculator</Link></li>
                  <li><Link to="/sinp-points-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">Saskatchewan SINP Calculator</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Other Countries</h4>
                <ul className="space-y-2">
                  <li><Link to="/australia-points-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">Australia Points Calculator</Link></li>
                  <li><Link to="/uk-points-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">UK Points Calculator</Link></li>
                  <li><Link to="/germany-chancenkarte-calculator" className="text-slate-400 hover:text-[#C5A028] text-sm">Germany Chancenkarte Calculator</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Resources</h4>
                <ul className="space-y-2">
                  <li><Link to="/visa-pre-assesment" className="text-slate-400 hover:text-[#C5A028] text-sm">Free Visa Assessment</Link></li>
                  <li><Link to="/contact" className="text-slate-400 hover:text-[#C5A028] text-sm">Contact Us</Link></li>
                  <li><Link to="/about" className="text-slate-400 hover:text-[#C5A028] text-sm">About Zafco HR</Link></li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-slate-800 text-center">
              <p className="text-slate-400 text-sm">
                All calculators provide preliminary assessments only. Results do not guarantee immigration approval.
                Consult with our experts for accurate evaluation.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default CalculatorsPage;
